import { useQuery } from '@apollo/client'
import { useEffectReducer } from 'use-effect-reducer'
import {
  LSPAYPairingData,
  LSPAYPairingRequest,
} from '../../helpers/lspay.types'
import {
  FAILED_PAIRING_CHECK,
  LSPAY_ERROR,
  LSPAY_ERROR_MESSAGES,
  SANDBOX_FLAG,
  SIMULATOR_SN,
} from '../../helpers/lspayConstants'
import { GET_PAIRING_DATA } from '../../queries/Pairings'
import { TAG, useTrackLSPAYError } from '../errorTracking'
import { useTrackLSPAYTiming } from '../timeTracking'
import { useShouldUseSimulator } from '../useShouldUseSimulator'
import { useVdFeatureManager } from '../../../../../ng-compat/hooks'
import { getPaymentEffectsMap } from './effects'
import { paymentStateReducer } from './reducer'
import { PaymentEvent, PaymentReducerArgs, PaymentState } from './types'

// Try finding the proper pairing and dispatch the resulting event
const usePairingWithDispatch = (
  registerId: string,
  isSimulated: boolean,
  dispatch: (event: PaymentEvent) => void,
  trackError: ReturnType<typeof useTrackLSPAYError>
) => {
  useQuery<LSPAYPairingData, LSPAYPairingRequest>(GET_PAIRING_DATA, {
    variables: { registerID: registerId },
    onCompleted: data => {
      // Handle the case where we have the simulator flag set to true but no registered pairings.
      if (isSimulated) {
        dispatch({
          type: 'PAIRING_OK',
          pairing: {
            sn: SIMULATOR_SN,
            label: 'simulator',
            isSimulated,
          },
        })
        return
      }

      const pairing = data.lspayPairings?.[0]
      if (pairing) {
        const { deviceSN, deviceLabel, deviceID, deviceType } = pairing

        dispatch({
          type: 'PAIRING_OK',
          pairing: {
            sn: deviceSN,
            label: deviceLabel,
            isSimulated: false,
            terminalID: deviceID,
            terminalModel: deviceType,
          },
        })
        return
      }

      dispatch({
        type: 'ERROR',
        errorMessage: LSPAY_ERROR_MESSAGES[LSPAY_ERROR.PAIRING_MISSING],
      })
    },
    onError: error => {
      // note the lspay pairing query does not return an error any more when no pairing is found
      dispatch({
        type: 'ERROR',
        errorMessage: LSPAY_ERROR_MESSAGES[LSPAY_ERROR.DEFAULT_PAYMENT],
      })
      trackError(FAILED_PAIRING_CHECK, error)
    },
  })
}

export const usePaymentEffectReducer = (args: PaymentReducerArgs) => {
  const { retailerPaymentType, register, transactionId, amount } = args

  const trackError = useTrackLSPAYError(TAG.payment, { transactionId })
  const trackTiming = useTrackLSPAYTiming({
    transactionId,
    paymentTypeId: retailerPaymentType.type_id,
    isRefund: amount.isNegative(),
  })
  const shouldUseSimulator = useShouldUseSimulator()
  const isSandbox = useVdFeatureManager().get(SANDBOX_FLAG)

  // The effectReducer we'll be returning
  const effectReducer = useEffectReducer(
    paymentStateReducer,
    _exec => {
      // Note the side-effect of fetching the pairing is done separately (see usage of usePairingWithDispatch)
      // Return the initial state
      return {
        status: 'fetchingPairing',
        retailerPaymentTypeName: retailerPaymentType.name,
      } as PaymentState
    },
    getPaymentEffectsMap({
      ...args,
      trackError,
      trackTiming,
      isSandbox,
    })
  )

  const [, dispatch] = effectReducer
  // This is launched immediately like a side-effect. Because it's a hook it cannot be launched as a
  // 'standard' effect of useEffectReducer. A lazy apollo query could have worked but apollo is not
  // triggering onComplete/onError callbacks when the response comes from the cache, so any payment
  // with LSPAY would be stalled except for the first one on page load.
  usePairingWithDispatch(register.id, shouldUseSimulator, dispatch, trackError)

  return effectReducer
}
